import { fetchWithProgress } from './fetchWithProgress';
import { modelCache } from './modelCache';
import dataCache from './dataCache';
import { clearCatalogueSnapshot } from './catalogueSnapshot';

export const LS = {
  appVersion: 'mdv.appVersion',
  buildId: 'mdv.buildId',
  cacheGeneration: 'mdv.cacheGeneration',
  modelsCacheGeneration: 'mdv.modelsCacheGeneration',
  offlineReady: 'mdv.offlineReady',
  offlineReadyAt: 'mdv.offlineReadyAt',
};

export const DEFAULT_GENERATION = 'mdv-offline-v1';

export function appCacheName(gen = getStoredGeneration()) {
  return `mdv-app-${gen}`;
}

export function modelsCacheName(gen = getStoredGeneration()) {
  return `mdv-models-${gen}`;
}

export function getStoredVersion() {
  return {
    appVersion: localStorage.getItem(LS.appVersion),
    buildId: localStorage.getItem(LS.buildId),
    cacheGeneration: localStorage.getItem(LS.cacheGeneration),
    modelsCacheGeneration: localStorage.getItem(LS.modelsCacheGeneration),
    offlineReady: localStorage.getItem(LS.offlineReady) === 'true',
    offlineReadyAt: localStorage.getItem(LS.offlineReadyAt),
  };
}

export function getStoredGeneration() {
  return localStorage.getItem(LS.cacheGeneration) || DEFAULT_GENERATION;
}

export function persistVersion(v) {
  if (v.appVersion != null) localStorage.setItem(LS.appVersion, v.appVersion);
  if (v.buildId != null) localStorage.setItem(LS.buildId, v.buildId);
  if (v.cacheGeneration != null) localStorage.setItem(LS.cacheGeneration, v.cacheGeneration);
  if (v.modelsCacheGeneration != null) {
    localStorage.setItem(LS.modelsCacheGeneration, v.modelsCacheGeneration);
  }
}

export function setOfflineReady(ready) {
  localStorage.setItem(LS.offlineReady, ready ? 'true' : 'false');
  if (ready) {
    localStorage.setItem(LS.offlineReadyAt, new Date().toISOString());
  } else {
    localStorage.removeItem(LS.offlineReadyAt);
  }
}

export async function fetchRemoteVersion() {
  const res = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('Failed to fetch version.json');
  return res.json();
}

export async function clearAllBrowserCaches() {
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  modelCache.clear();
  clearCatalogueSnapshot();
}

export async function clearModelsCache(generation) {
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith('mdv-models-') || k.startsWith('dv-models-') || (generation && k === modelsCacheName(generation)))
        .map((k) => caches.delete(k))
    );
  }
  modelCache.clear();
  setOfflineReady(false);
}

export async function clearAppCachesExcept(keepGeneration) {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(
    keys
      .filter((k) => (k.startsWith('mdv-app-') || k.startsWith('dv-app-')) && k !== appCacheName(keepGeneration))
      .map((k) => caches.delete(k))
  );
}

export async function ensureGenerationBuckets(generation) {
  if (!('caches' in window)) return;
  await caches.open(appCacheName(generation));
  await caches.open(modelsCacheName(generation));
}

export function needsFirstTimeWipe(stored, remote) {
  const gen = stored.cacheGeneration;
  if (!gen) return true;
  // Wipe legacy DataViewer (dv-*) offline generations
  if (String(gen).startsWith('dv-offline-') || String(gen).startsWith('dv-')) return true;
  if (!String(gen).startsWith('mdv-offline-')) return true;
  if (remote?.cacheGeneration === DEFAULT_GENERATION && !stored.appVersion) return true;
  return false;
}

export function isRemoteNewer(stored, remote) {
  if (!remote) return false;
  if (!stored.appVersion && !stored.buildId) return false;
  if (remote.buildId && stored.buildId && remote.buildId !== stored.buildId) return true;
  if (remote.cacheGeneration && stored.cacheGeneration && remote.cacheGeneration !== stored.cacheGeneration) {
    return true;
  }
  if (remote.appVersion && stored.appVersion && remote.appVersion !== stored.appVersion) return true;
  return false;
}

function urlPathname(url) {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://local').pathname;
  } catch {
    return String(url || '').split('?')[0];
  }
}

function filePathTail(input) {
  return String(input || '')
    .replace(/\\/g, '/')
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+/, '')
    .replace(/^models\//i, '')
    .toLowerCase();
}

function cacheEntryMatchesFile(reqUrl, needle) {
  const cached = filePathTail(urlPathname(reqUrl) || reqUrl);
  const want = filePathTail(needle);
  if (!want) return false;
  return cached === want || cached.endsWith(`/${want}`);
}

export async function invalidateModelPath(pathOrUrl, { replaceWithUrl } = {}) {
  const raw = String(pathOrUrl || '');
  if (!raw && !replaceWithUrl) return;
  const needle = raw.replace(/^\.\//, '').split('?')[0];
  if (needle) {
    modelCache.delete(`gltf_${needle}`);
    modelCache.delete(`gltf_${raw}`);
    modelCache.deleteMatching(needle);
    modelCache.deleteMatching(filePathTail(needle));
    Object.keys(dataCache).forEach((k) => {
      if (cacheEntryMatchesFile(k, needle)) delete dataCache[k];
    });
  }
  if ('caches' in window) {
    const gen = getStoredGeneration();
    const cache = await caches.open(modelsCacheName(gen));
    const keys = await cache.keys();
    await Promise.all(
      keys
        .filter((req) => needle && cacheEntryMatchesFile(req.url, needle))
        .map((req) => cache.delete(req))
    );
    const freshUrl = replaceWithUrl || (raw.includes('?') ? raw : null);
    if (freshUrl) {
      try {
        const res = await fetch(freshUrl, { credentials: 'same-origin', cache: 'reload' });
        if (res.ok) await cache.put(freshUrl, res.clone());
      } catch (e) {
        console.warn('[invalidateModelPath] re-cache failed', freshUrl, e);
      }
    }
  }
}

/**
 * Look up a model URL in Cache Storage (exact ?v= first).
 * @returns {Promise<Response|null>}
 */
export async function matchCachedModel(url, { ignoreSearch = false } = {}) {
  if (!url || !('caches' in window)) return null;
  try {
    const cache = await caches.open(modelsCacheName());
    const exact = await cache.match(url, { ignoreSearch: false });
    if (exact) return exact;
    if (ignoreSearch) {
      return (await cache.match(url, { ignoreSearch: true })) || null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Pathnames currently stored in the models cache (query string ignored). */
export async function listCachedModelPaths() {
  const paths = new Set();
  if (!('caches' in window)) return paths;
  try {
    const cache = await caches.open(modelsCacheName());
    const keys = await cache.keys();
    keys.forEach((req) => {
      const path = urlPathname(req.url);
      if (path) paths.add(path);
    });
  } catch {
    /* ignore */
  }
  return paths;
}

export function urlIsInCachedPaths(url, cachedPaths) {
  if (!url || !cachedPaths?.size) return false;
  return cachedPaths.has(urlPathname(url));
}

/** Store a successful model Response under the models generation bucket. */
export async function putCachedModel(url, response) {
  if (!url || !response || !('caches' in window)) return;
  try {
    if (!response.ok) return;
    const cache = await caches.open(modelsCacheName());
    // Drop older ?v= variants for the same path so storage does not grow forever
    try {
      const u = new URL(url, window.location.origin);
      const basePath = u.origin + u.pathname;
      const keys = await cache.keys();
      await Promise.all(
        keys
          .filter((req) => {
            try {
              const k = new URL(req.url);
              return k.origin + k.pathname === basePath && req.url !== url;
            } catch {
              return false;
            }
          })
          .map((req) => cache.delete(req))
      );
    } catch {
      /* ignore cleanup errors */
    }
    await cache.put(url, response.clone());
  } catch (e) {
    console.warn('[putCachedModel] failed', url, e);
  }
}

/**
 * Cache-first model bytes for GLTFLoader.parse — works even when the SW is not controlling.
 * On network hit, writes into Cache Storage so reload can reuse the file.
 */
export async function getModelArrayBuffer(url, { signal, onProgress } = {}) {
  if (!url) throw new Error('Missing model URL');

  const exact = await matchCachedModel(url);
  if (exact) {
    onProgress?.({ percent: 100, loaded: 1, total: 1, phase: 'cache', cached: true, indeterminate: false });
    return exact.arrayBuffer();
  }

  try {
    const { buffer, contentType } = await fetchWithProgress(url, { signal, onProgress, cache: 'no-store' });
    await putCachedModel(
      url,
      new Response(buffer, {
        status: 200,
        headers: {
          'Content-Type': contentType,
          'Content-Length': String(buffer.byteLength),
        },
      })
    );
    return buffer;
  } catch (err) {
    const loose = await matchCachedModel(url, { ignoreSearch: true });
    if (loose) {
      onProgress?.({ percent: 100, loaded: 1, total: 1, phase: 'cache', cached: true, indeterminate: false });
      return loose.arrayBuffer();
    }
    throw err;
  }
}

/**
 * Precache a list of URLs into the models cache (concurrency limited).
 */
export function decoderAssetUrls() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const gltf = `${base}/draco/gltf`;
  return [
    `${gltf}/draco_wasm_wrapper.js`,
    `${gltf}/draco_decoder.wasm`,
    `${gltf}/draco_decoder.js`,
  ];
}

export async function precacheAppAssets(urls, { onProgress, signal } = {}) {
  if (!('caches' in window)) return { done: 0, total: 0 };
  const cache = await caches.open(appCacheName());
  const list = [...new Set((urls || []).filter(Boolean))];
  let done = 0;
  for (const url of list) {
    if (signal?.aborted) throw new Error('cancelled');
    try {
      const existing = await cache.match(url, { ignoreSearch: true });
      if (!existing) {
        const res = await fetch(url, { credentials: 'same-origin', cache: 'reload' });
        if (res.ok) await cache.put(url, res.clone());
      }
    } catch (e) {
      console.warn('[precacheApp] failed', url, e);
    }
    done += 1;
    onProgress?.({ done, total: list.length, url });
  }
  return { done, total: list.length };
}

export async function precacheUrls(urls, { concurrency = 2, onProgress, signal } = {}) {
  const gen = getStoredGeneration();
  const cache = await caches.open(modelsCacheName(gen));
  const list = [...new Set(urls.filter(Boolean))];
  let done = 0;
  let cursor = 0;

  async function worker() {
    while (cursor < list.length) {
      if (signal?.aborted) throw new Error('cancelled');
      const i = cursor++;
      const url = list[i];
      try {
        const existing = await cache.match(url, { ignoreSearch: false });
        if (existing) {
          done += 1;
          onProgress?.({ done, total: list.length, url });
          continue;
        }
        const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (res.ok) await putCachedModel(url, res);
      } catch (e) {
        console.warn('[precache] failed', url, e);
      }
      done += 1;
      onProgress?.({ done, total: list.length, url });
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, list.length) }, () => worker());
  await Promise.all(workers);
  return { done, total: list.length };
}

export async function activateAppUpdate(remote) {
  await clearModelsCache();
  await clearAppCachesExcept(remote.cacheGeneration || DEFAULT_GENERATION);
  persistVersion({
    appVersion: remote.appVersion,
    buildId: remote.buildId,
    cacheGeneration: remote.cacheGeneration || DEFAULT_GENERATION,
    modelsCacheGeneration: remote.cacheGeneration || DEFAULT_GENERATION,
  });
  setOfflineReady(false);
  await ensureGenerationBuckets(remote.cacheGeneration || DEFAULT_GENERATION);

  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
  const regs = await navigator.serviceWorker?.getRegistrations?.();
  regs?.forEach((r) => r.waiting?.postMessage?.({ type: 'SKIP_WAITING' }));

  window.location.reload();
}

export async function performFirstTimeWipe(remote) {
  await clearAllBrowserCaches();
  Object.values(LS).forEach((k) => localStorage.removeItem(k));
  const gen = remote?.cacheGeneration || DEFAULT_GENERATION;
  persistVersion({
    appVersion: remote?.appVersion || '1.0.0',
    buildId: remote?.buildId || new Date().toISOString(),
    cacheGeneration: gen,
    modelsCacheGeneration: gen,
  });
  setOfflineReady(false);
  await ensureGenerationBuckets(gen);
  return gen;
}
